"use client";

import { serverTimestamp } from "firebase/firestore";
import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EventSubmissions from "../components/events/EventSubmissions";
import LibrarySelectionModal from "../components/events/LibrarySelectionModal";
import PastEvents from "../components/events/PastEvents";
import dreamWorldProvider from "../DreamWorldProvider";
import { selectUserGlobalData } from "../store/dreamworldSlice";
import { Submission } from "../types/events";
import { LibraryItem } from "../types/library";
import PageLayout from "../components/ui/PageLayout";

export interface OngoingEventInfo {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  bannerImage: string;
  startDate: Date;
  endDate: Date;
  submissionDeadline: Date;
  winnerAnnouncement: Date;
  timezone: string;
  requirements: string[];
  prizes: {
    position: string;
    winners: number;
    reward: string;
    credits?: number;
  }[];
  evaluationDimensions: {
    name: string;
    description: string;
  }[];
  rules: string[];
  prizeGiving: string[];
  howToParticipate: string[];
  status: "upcoming" | "live" | "ended";
  submissionCount?: number;
}

const EventsPage: React.FC = () => {
  const router = useNavigate();
  const [ongoingEvent, setOngoingEvent] = useState<OngoingEventInfo | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [isSubmissionPanelOpen, setIsSubmissionPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "details" | "submissions" | "past"
  >("details");
  const user = useSelector(selectUserGlobalData);

  const onSubmitToEvent = async (item: LibraryItem) => {
    if (!user || !ongoingEvent) return;

    try {
      const submissionData: Omit<Submission, "id"> = {
        eventId: ongoingEvent.id,
        userId: user.userData?.uid,
        contentUrl: item.fullUrl,
        title: item.title,
        description: item.description || "",
        author: user.userData?.username || "Anonymous",
        image: item.thumbnailUrl,
        createdAt: serverTimestamp() as any,
        likes: 0,
        views: 0,
        comments: 0,
      };

      await dreamWorldProvider.submitToEvent(submissionData);

      setIsSubmissionPanelOpen(false);
      alert("Submission successful!");
    } catch (error) {
      console.error("Error submitting to event:", error);
    }
  };

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const { events } = await dreamWorldProvider.getEvents(undefined, 1);
        if (events.length > 0) {
          const eventData = events[0];
          const mappedEvent: OngoingEventInfo = eventData;
          setOngoingEvent(mappedEvent);
        }
      } catch (error) {
        console.error("Error fetching ongoing event:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, []);

  useEffect(() => {
    if (!ongoingEvent) return;

    const updateTimeLeft = () => {
      const now = new Date();
      const deadline = ongoingEvent.submissionDeadline;
      const diff = deadline.getTime() - now.getTime();

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        setTimeLeft(`${days}d ${hours}h left`);
      } else {
        setTimeLeft("Ended");
      }
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000 * 60);

    return () => clearInterval(interval);
  }, [ongoingEvent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 flex items-center justify-center">
        <div className="animate-pulse">
          <div className="w-8 h-8 bg-purple-500 rounded-full animate-bounce"></div>
        </div>
      </div>
    );
  }

  if (!ongoingEvent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎭</div>
          <div className="text-xl text-gray-400">No ongoing events</div>
        </div>
      </div>
    );
  }

  const getStatusColor = () => {
    switch (ongoingEvent.status) {
      case "live":
        return "text-green-400 bg-green-400/20";
      case "upcoming":
        return "text-blue-400 bg-blue-400/20";
      case "ended":
        return "text-gray-400 bg-gray-400/20";
      default:
        return "text-purple-400 bg-purple-400/20";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 text-white">
      {isSubmissionPanelOpen && (
        <LibrarySelectionModal
          onClose={() => setIsSubmissionPanelOpen(false)}
          onSelect={onSubmitToEvent}
        />
      )}

      {/* Compact Header */}
      <div className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700">
        <div className="flex justify-between items-center p-4 max-w-7xl mx-auto">
          <button
            onClick={() => router(-1)}
            className="p-2 hover:bg-slate-800 rounded-lg transition-all duration-200 group"
          >
            <svg
              className="w-5 h-5 group-hover:scale-110 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="flex items-center gap-3">
            <div
              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}
            >
              {ongoingEvent.status.toUpperCase()}
            </div>
            {timeLeft && (
              <div className="hidden sm:flex items-center gap-2 text-sm bg-slate-800/60 px-3 py-1 rounded-full">
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                {timeLeft}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => router("/create")}
              className="px-4 py-2 bg-slate-800/60 hover:bg-slate-700 border border-slate-600/50 rounded-lg transition-all duration-200 text-sm"
            >
              Create
            </button>
            <button
              onClick={() => setIsSubmissionPanelOpen(true)}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 rounded-lg transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-green-500/25"
            >
              Submit Entry
            </button>
          </div>
        </div>
      </div>

      <PageLayout>
        {/* Hero Section */}
        <div className="relative mb-8 rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent z-10"></div>
          <img
            src={ongoingEvent.bannerImage || "/placeholder.svg"}
            alt={ongoingEvent.title}
            className="w-full h-80 sm:h-96 object-cover"
          />
          <div className="absolute inset-0 z-20 flex items-end p-8">
            <div className="max-w-2xl">
              <h1 className="text-4xl sm:text-5xl font-bold mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                {ongoingEvent.title}
              </h1>
              {ongoingEvent.subtitle && (
                <p className="text-xl text-gray-200 mb-4">
                  {ongoingEvent.subtitle}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300">
                <span className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {ongoingEvent.startDate.toLocaleDateString()} -{" "}
                  {ongoingEvent.endDate.toLocaleDateString()}
                </span>
                {ongoingEvent.submissionCount && (
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    {ongoingEvent.submissionCount} submissions
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-700 mb-6">
          {[
            { id: "details", label: "Event Details", icon: "📋" },
            { id: "submissions", label: "Submissions", icon: "🎨" },
            { id: "past", label: "Past Events", icon: "📚" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                activeTab === tab.id
                  ? "text-purple-400 border-purple-400"
                  : "text-gray-400 border-transparent hover:text-white hover:border-slate-600"
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "details" && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                <h2 className="text-xl font-semibold mb-4 text-purple-300">
                  About This Event
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  {ongoingEvent.description}
                </p>
              </div>

              {/* Requirements & How to Participate */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-blue-400">📝</span>
                    How to Participate
                  </h3>
                  <div className="space-y-2">
                    {ongoingEvent.howToParticipate.map((step, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2 text-sm text-gray-300"
                      >
                        <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-xs text-blue-400 mt-0.5 flex-shrink-0">
                          {index + 1}
                        </div>
                        {step}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="text-purple-400">✅</span>
                    Requirements
                  </h3>
                  <div className="space-y-2">
                    {ongoingEvent.requirements.map((req, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2 text-sm text-gray-300"
                      >
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                        {req}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Evaluation Dimensions */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="text-indigo-400">📊</span>
                  Evaluation Criteria
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {ongoingEvent.evaluationDimensions.map((dimension, index) => (
                    <div
                      key={index}
                      className="bg-slate-900/50 rounded-lg p-4 border border-slate-600/30"
                    >
                      <h4 className="font-medium text-white mb-2">
                        {dimension.name}
                      </h4>
                      <p className="text-sm text-gray-400">
                        {dimension.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rules */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="text-yellow-400">⚖️</span>
                  Contest Rules
                </h3>
                <div className="grid gap-2">
                  {ongoingEvent.rules.map((rule, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 text-sm text-gray-300 p-2 rounded-lg hover:bg-slate-700/30 transition-colors"
                    >
                      <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                      {rule}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Timeline */}
              <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="text-blue-400">⏰</span>
                  Timeline
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <div>
                      <div className="text-sm font-medium text-green-400">
                        Submission Period
                      </div>
                      <div className="text-xs text-gray-400">
                        {ongoingEvent.startDate.toLocaleDateString()} -{" "}
                        {ongoingEvent.endDate.toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <div>
                      <div className="text-sm font-medium text-purple-400">
                        Winner Announcement
                      </div>
                      <div className="text-xs text-gray-400">
                        {ongoingEvent.winnerAnnouncement.toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prizes */}
              <div className="bg-gradient-to-br from-amber-900/30 to-yellow-900/30 rounded-xl p-6 border border-amber-700/30">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="text-yellow-400">🏆</span>
                  Prizes
                </h3>
                <div className="space-y-3">
                  {ongoingEvent.prizes.map((prize, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-black/20 rounded-lg"
                    >
                      <span className="text-2xl">
                        {index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-yellow-200">
                          {prize.position} ({prize.winners} winner
                          {prize.winners > 1 ? "s" : ""})
                        </div>
                        <div className="text-xs text-gray-300">
                          {prize.reward}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="text-xs text-amber-300 bg-black/20 rounded-lg p-3 mt-3">
                    ✨ Featured entries may appear on the Kling AI homepage!
                  </div>
                </div>
              </div>

              {/* Prize Distribution */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="text-red-400">🎁</span>
                  Prize Distribution
                </h3>
                <div className="space-y-2">
                  {ongoingEvent.prizeGiving.map((info, index) => (
                    <div
                      key={index}
                      className="text-sm text-gray-300 flex items-start gap-2"
                    >
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                      {info}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "submissions" && (
          <div>
            <EventSubmissions eventId={ongoingEvent.id} />
          </div>
        )}

        {activeTab === "past" && (
          <div>
            <PastEvents />
          </div>
        )}
      </PageLayout>
    </div>
  );
};

export default EventsPage;
